import { AgentAnalyticsData, AgentAnalyticsTimePoint, AnalyticsTimeframe } from "../../../redux/reducers/agentAnalyticsSlice";

export type AgentDashboardPoint = {
  time: string;
  sessions: number;
  errorRate: number;
  transferRate: number;
  ttsAverage: number;
  ttsP50: number;
  ttsP95: number;
  sttAverage: number;
  sttP50: number;
  sttP95: number;
  turnaroundAverage: number;
  turnaroundP50: number;
  turnaroundP95: number;
  sessionLengthAverage: number;
  sessionLengthP50: number;
  sessionLengthP95: number;
  turnDepthAverage: number;
};

const previewTimeframeConfig: Record<AnalyticsTimeframe, { points: number; stepMs: number }> = {
  "15m": { points: 16, stepMs: 60 * 1000 },
  "30m": { points: 16, stepMs: 2 * 60 * 1000 },
  "1h": { points: 13, stepMs: 5 * 60 * 1000 },
  "3h": { points: 19, stepMs: 10 * 60 * 1000 },
  "6h": { points: 25, stepMs: 15 * 60 * 1000 },
  "12h": { points: 25, stepMs: 30 * 60 * 1000 },
  "24h": { points: 25, stepMs: 60 * 60 * 1000 },
  "3d": { points: 19, stepMs: 4 * 60 * 60 * 1000 },
  "7d": { points: 8, stepMs: 24 * 60 * 60 * 1000 },
  "30d": { points: 30, stepMs: 24 * 60 * 60 * 1000 },
  custom: { points: 25, stepMs: 60 * 60 * 1000 },
};

const average = (values: number[]): number => {
  if (values.length === 0) return 0;
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

export const buildPreviewAnalytics = (agentId: string, timeframe: AnalyticsTimeframe): AgentAnalyticsData => {
  const { points, stepMs } = previewTimeframeConfig[timeframe];
  const endTime = Date.now();
  const timePoints: AgentAnalyticsTimePoint[] = Array.from({ length: points }, (_, index) => {
    const progress = index / Math.max(points - 1, 1);
    const wave = Math.sin(index * 0.72) * 7 + Math.cos(index * 0.31) * 4;
    const sessions = Math.max(8, Math.round(31 + progress * 15 + wave));
    const ttsAverage = Math.max(175, 282 + Math.sin(index * 0.58) * 34 + progress * 12);
    const sttAverage = Math.max(120, 201 + Math.cos(index * 0.47) * 25 - progress * 8);
    const turnaroundAverage = Math.max(460, 720 + Math.sin(index * 0.41) * 86 - progress * 35);
    const sessionLengthAverage = Math.max(1.1, 3.4 + Math.sin(index * 0.36) * 0.55 + progress * 0.25);
    const turnDepthAverage = Math.max(2, 7.4 + Math.cos(index * 0.4) * 1.1 + progress * 0.4);
    const errorRate = Math.max(0.4, 2.4 + Math.sin(index * 0.51) * 0.7 - progress * 0.35);
    const transferRate = Math.max(1.2, 7.2 + Math.cos(index * 0.34) * 1.4 + progress * 0.2);
    const timestamp = new Date(endTime - (points - 1 - index) * stepMs).toISOString();

    return {
      timestamp,
      sessions: { count: sessions },
      ttsLatency: { avg: ttsAverage, p50: ttsAverage * 0.82, p95: ttsAverage * 1.62 },
      sttLatency: { avg: sttAverage, p50: sttAverage * 0.8, p95: sttAverage * 1.55 },
      firstResponseTime: { avg: turnaroundAverage, p50: turnaroundAverage * 0.78, p95: turnaroundAverage * 1.54 },
      turnAroundTime: { avg: turnaroundAverage, p50: turnaroundAverage * 0.78, p95: turnaroundAverage * 1.54 },
      sessionLength: { avg: sessionLengthAverage, p50: sessionLengthAverage * 0.76, p95: sessionLengthAverage * 1.72 },
      errorRate,
      transferRate,
      turnDepth: { avg: turnDepthAverage, p50: turnDepthAverage * 0.78, p95: turnDepthAverage * 1.48 },
    };
  });

  return {
    agentId,
    startDate: timePoints[0].timestamp,
    endDate: timePoints[timePoints.length - 1].timestamp,
    timeframe,
    totalSessions: timePoints.reduce((sum, point) => sum + point.sessions.count, 0),
    metrics: {
      avgFirstTtsLatencyMs: average(timePoints.map((point) => point.ttsLatency.avg)),
      avgSttLatencyMs: average(timePoints.map((point) => point.sttLatency.avg)),
      avgTurnAroundTimeMs: average(timePoints.map((point) => point.turnAroundTime.avg)),
      errorRate: average(timePoints.map((point) => point.errorRate)),
      avgSessionLengthMinutes: average(timePoints.map((point) => point.sessionLength.avg)),
      transferRate: average(timePoints.map((point) => point.transferRate)),
      avgTurnDepth: average(timePoints.map((point) => point.turnDepth.avg)),
    },
    timePoints,
  };
};

type AnalyticsBucketSize = "minute" | "hour" | "day";

export const getAnalyticsBucketSize = (timeframe: AnalyticsTimeframe): AnalyticsBucketSize => {
  if (["15m", "30m"].includes(timeframe)) return "minute";
  if (["1h", "3h", "6h", "12h", "24h"].includes(timeframe)) return "hour";
  return "day";
};

const getBucketKey = (timestamp: string, bucketSize: AnalyticsBucketSize): string => {
  if (bucketSize === "day") return timestamp.substring(0, 10);
  if (bucketSize === "hour") return `${timestamp.substring(0, 13)}:00`;
  return timestamp.substring(0, 16);
};

const emptyTimePoint = (timestamp: string): AgentAnalyticsTimePoint => ({
  timestamp,
  sessions: { count: 0 },
  ttsLatency: { avg: 0, p50: 0, p95: 0 },
  sttLatency: { avg: 0, p50: 0, p95: 0 },
  firstResponseTime: { avg: 0, p50: 0, p95: 0 },
  turnAroundTime: { avg: 0, p50: 0, p95: 0 },
  sessionLength: { avg: 0, p50: 0, p95: 0 },
  errorRate: 0,
  transferRate: 0,
  turnDepth: { avg: 0, p50: 0, p95: 0 },
});

export const fillMissingAnalyticsBuckets = (
  timePoints: AgentAnalyticsTimePoint[],
  timeframe: AnalyticsTimeframe,
  rangeStart?: string,
  rangeEnd?: string,
): AgentAnalyticsTimePoint[] => {
  const bucketSize = getAnalyticsBucketSize(timeframe);
  const sortedPoints = [...timePoints].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  const start = rangeStart || sortedPoints[0]?.timestamp;
  const end = rangeEnd || sortedPoints[sortedPoints.length - 1]?.timestamp;
  if (!start || !end) return [];

  const dataByBucket = new Map(sortedPoints.map((point) => [getBucketKey(point.timestamp, bucketSize), point]));
  const filledPoints: AgentAnalyticsTimePoint[] = [];

  if (bucketSize === "day") {
    const [startYear, startMonth, startDay] = start.substring(0, 10).split("-").map(Number);
    const [endYear, endMonth, endDay] = end.substring(0, 10).split("-").map(Number);
    const cursor = new Date(startYear, startMonth - 1, startDay);
    const finalDate = new Date(endYear, endMonth - 1, endDay);

    while (cursor <= finalDate) {
      const dateKey = `${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`;
      filledPoints.push(dataByBucket.get(dateKey) || emptyTimePoint(dateKey));
      cursor.setDate(cursor.getDate() + 1);
    }
    return filledPoints;
  }

  const incrementMs = bucketSize === "minute" ? 60 * 1000 : 60 * 60 * 1000;
  const cursor = new Date(start);
  const finalTime = new Date(end).getTime();
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(finalTime)) return sortedPoints;

  if (bucketSize === "hour") cursor.setUTCMinutes(0, 0, 0);
  else cursor.setUTCSeconds(0, 0);

  while (cursor.getTime() <= finalTime) {
    const timestamp = cursor.toISOString();
    const bucketKey = getBucketKey(timestamp, bucketSize);
    filledPoints.push(dataByBucket.get(bucketKey) || emptyTimePoint(timestamp));
    cursor.setTime(cursor.getTime() + incrementMs);
  }

  return filledPoints;
};

export const toDashboardPoints = (timePoints: AgentAnalyticsTimePoint[]): AgentDashboardPoint[] => {
  return [...timePoints]
    .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
    .map((point) => ({
      time: point.timestamp,
      sessions: point.sessions.count,
      errorRate: point.errorRate,
      transferRate: point.transferRate,
      ttsAverage: point.ttsLatency.avg,
      ttsP50: point.ttsLatency.p50,
      ttsP95: point.ttsLatency.p95,
      sttAverage: point.sttLatency.avg,
      sttP50: point.sttLatency.p50,
      sttP95: point.sttLatency.p95,
      turnaroundAverage: point.turnAroundTime.avg,
      turnaroundP50: point.turnAroundTime.p50,
      turnaroundP95: point.turnAroundTime.p95,
      sessionLengthAverage: point.sessionLength.avg,
      sessionLengthP50: point.sessionLength.p50,
      sessionLengthP95: point.sessionLength.p95,
      turnDepthAverage: point.turnDepth.avg,
    }));
};

export const averageMetric = (points: AgentDashboardPoint[], selector: (point: AgentDashboardPoint) => number): number => {
  return average(points.map(selector));
};

export const compareHalves = (points: AgentDashboardPoint[], selector: (point: AgentDashboardPoint) => number, mode: "sum" | "average" = "average"): number => {
  if (points.length < 2) return 0;
  const halfSize = Math.floor(points.length / 2);
  const previousValues = points.slice(0, halfSize).map(selector);
  const currentValues = points.slice(points.length - halfSize).map(selector);
  const previous = mode === "sum" ? previousValues.reduce((sum, value) => sum + value, 0) : average(previousValues);
  const current = mode === "sum" ? currentValues.reduce((sum, value) => sum + value, 0) : average(currentValues);

  if (previous === 0) return 0;
  return ((current - previous) / previous) * 100;
};
